import React, { useEffect, useRef } from "react";
import { ClockValue, sanitizeClockPart } from "./clock";

interface ClockEntryProps {
  value: ClockValue;
  onChange: (value: ClockValue) => void;
}

/**
 * Minutes and seconds as two short numeric fields. `inputMode="numeric"`
 * opens the phone's number keypad instead of the full keyboard, and filling
 * the minutes moves straight on to the seconds.
 */
function ClockEntry({ value, onChange }: ClockEntryProps) {
  const minutesRef = useRef<HTMLInputElement>(null);
  const secondsRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    minutesRef.current?.focus();
  }, []);

  const handleMinutesChange = (raw: string) => {
    const minutes = sanitizeClockPart(raw);
    onChange({ ...value, minutes });

    if (minutes.length === 2) {
      secondsRef.current?.focus();
    }
  };

  return (
    <>
      <div className="ee-clock">
        <div className="ee-clock-field">
          <input
            ref={minutesRef}
            className="ee-clock-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            placeholder="00"
            aria-label="Minutes"
            value={value.minutes}
            onChange={(event) => handleMinutesChange(event.target.value)}
          />
          <span className="ee-clock-caption">Minutes</span>
        </div>

        <span className="ee-clock-colon" aria-hidden="true">
          :
        </span>

        <div className="ee-clock-field">
          <input
            ref={secondsRef}
            className="ee-clock-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            placeholder="00"
            aria-label="Seconds"
            value={value.seconds}
            onChange={(event) =>
              onChange({
                ...value,
                seconds: sanitizeClockPart(event.target.value),
              })
            }
          />
          <span className="ee-clock-caption">Seconds</span>
        </div>
      </div>

      <p className="ee-hint">
        The clock counts down — enter the time it showed.
      </p>
    </>
  );
}

export default ClockEntry;
